/**
 * ContractStatusDot — Phase 6a.10 (introduced) / 6a.11 (server-rejection)
 * / 6a.12/F-2 (out-of-period cut-off) / 6a.12/F-3 (dedicated period-violation
 * tier).
 *
 * Renders a single per-contract status indicator (○ / ◐ / ● / ⚠ / ✗) with a
 * shadcn Tooltip showing either the status label or contextual errors.
 * Click navigates to that contract's entry page.
 *
 * Tooltip body is driven by the already-resolved status (priority is encoded
 * upstream in `deriveContractStatus` per Spec §21.7.3 v1.13):
 *   • server-rejected → backend Zod messages verbatim
 *   • period-violation → out-of-period message (Phase 6a.12/F-3)
 *   • partial → frontend Zod errors
 *   • filled / empty → no body, just header
 *
 * Visual distinction within the red tier: AlertTriangle for period-violation
 * (conceptual error), X for server-rejected (technical rejection). Same
 * `text-destructive` color, different icon shape.
 */

import { X as XIcon, AlertTriangle as AlertTriangleIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ContractInput } from "@/types";

import {
  deriveContractStatus,
  getContractValidationErrors,
  getOutOfPeriodMessageKey,
  type ContractStatus,
} from "../contract-status";

interface ContractStatusDotProps {
  /** 1-based contract index for display (e.g. "Sözleşme 3"). */
  contractIndex: number;
  contract: ContractInput | null;
  isDirty: boolean;
  isCurrent: boolean;
  /**
   * Phase 6a.23 — engagement functional currency. Threaded through to
   * `deriveContractStatus` so FX-conditional schema validation judges
   * non-TRY engagements correctly. Optional for backwards-compat.
   */
  functionalCurrency?: string;
  /** Backend Zod errors for this slot from the last Calculate attempt. */
  serverRejection?: string[];
  /**
   * Engagement audit-period bounds (Phase 6a.12/F-2). When provided, an
   * otherwise Zod-clean contract whose `[commencement, commencement +
   * leaseTermMonths]` interval lies entirely outside this range is
   * marked `partial` with an out-of-period tooltip message.
   */
  auditPeriod?: { start: string; end: string };
  onClick?: (idx: number) => void;
}

function symbolForStatus(status: ContractStatus): React.ReactNode {
  switch (status) {
    case "filled":
      return "●";
    case "partial":
      return "◐";
    case "period-violation":
      return <AlertTriangleIcon className="h-4 w-4" aria-hidden />;
    case "server-rejected":
      return <XIcon className="h-4 w-4" aria-hidden />;
    case "empty":
    default:
      return "○";
  }
}

function colorClassForStatus(status: ContractStatus): string {
  switch (status) {
    case "filled":
      return "text-emerald-600 dark:text-emerald-400";
    case "partial":
      return "text-amber-600 dark:text-amber-400";
    case "period-violation":
    case "server-rejected":
      return "text-destructive";
    case "empty":
    default:
      return "text-muted-foreground";
  }
}

export function ContractStatusDot({
  contractIndex,
  contract,
  isDirty,
  isCurrent,
  serverRejection,
  auditPeriod,
  functionalCurrency,
  onClick,
}: ContractStatusDotProps) {
  const { t } = useTranslation();
  const status = deriveContractStatus({
    contract,
    isDirty,
    serverRejection,
    auditPeriod,
    functionalCurrency,
  });

  // Tooltip body source per resolved status. Priority is encoded upstream
  // in `deriveContractStatus` (Spec §21.7.3 v1.13), so this is a flat
  // switch — no nested conditionals.
  const tooltipErrors: string[] = (() => {
    switch (status) {
      case "server-rejected":
        return serverRejection ?? [];
      case "period-violation": {
        if (!auditPeriod) return [];
        // The `as never` cast bridges the strict-typed i18n keys union.
        const key = getOutOfPeriodMessageKey(contract, auditPeriod);
        return key ? [t(key as never) as string] : [];
      }
      case "partial":
        return getContractValidationErrors(contract, functionalCurrency);
      case "filled":
      case "empty":
      default:
        return [];
    }
  })();

  // Status label shown when there are no body messages (filled/empty/current),
  // or as the default header source. The aria-label always uses this.
  const statusLabel = (() => {
    if (isCurrent) return t("engagement.contract.status.currentLabel");
    switch (status) {
      case "filled":
        return t("engagement.contract.status.filled");
      case "partial":
        return t("engagement.contract.status.partial");
      case "period-violation":
        return t("engagement.contract.status.periodViolation");
      case "server-rejected":
        return t("engagement.contract.status.serverRejected");
      case "empty":
      default:
        return t("engagement.contract.status.empty");
    }
  })();

  const tooltipHeader =
    status === "server-rejected"
      ? t("engagement.contract.status.serverRejectedHeader")
      : statusLabel;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onClick?.(contractIndex - 1)}
          aria-label={`${contractIndex}: ${statusLabel}`}
          data-testid={`contract-status-dot-${contractIndex}`}
          data-status={status}
          data-current={isCurrent || undefined}
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-full text-base",
            "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            colorClassForStatus(status),
            isCurrent && "ring-2 ring-primary bg-primary/10",
          )}
        >
          <span aria-hidden>{symbolForStatus(status)}</span>
          <span className="sr-only">
            {contractIndex}: {statusLabel}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="center" className="max-w-xs">
        <div className="text-xs">
          <div className="font-medium">
            #{contractIndex} — {tooltipHeader}
          </div>
          {tooltipErrors.length > 0 && (
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {tooltipErrors.slice(0, 6).map((e, i) => (
                <li key={`${e}-${i}`}>{e}</li>
              ))}
              {tooltipErrors.length > 6 && (
                <li className="opacity-70">
                  …+{tooltipErrors.length - 6}
                </li>
              )}
            </ul>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
